import { Grid, Group, Stack } from "@mantine/core";
import { Control, Controller, FieldPath, FieldValues, PathValue, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CTA from "@/components/atoms/CTA";
import Text from "@/components/atoms/Text";
import TextInput from "@/components/atoms/TextInput";
import { useCurrentPosition } from "@/hooks/useCurrentPosition";
import { formatAccuracy, formatCoordinate, isCoarseAccuracy, parseCoordinatePair } from "@/utils/coordinates";

export interface CoordinateFieldsProps<T extends FieldValues> {
  control: Control<T>;
  latitudeName: FieldPath<T>;
  longitudeName: FieldPath<T>;
  setValue: UseFormSetValue<T>;
  disabled?: boolean;
  /**
   * Called with the GPS fix time after a browser capture, and with null on any manual edit so a
   * stale capture timestamp does not stick to hand-typed values.
   */
  onCaptured?: (capturedAt: string | null) => void;
}

/**
 * Values are held as free text rather than numbers so that a comma decimal separator (`59,9139`)
 * and a pasted "lat, lon" pair both work. Parsing happens at the mapper boundary via
 * `buildPickupCoordinates`.
 *
 * Note: these fields are deliberately never gated on whether an address has been entered —
 * coordinates are independent of the address.
 */
function CoordinateFields<T extends FieldValues>({
  control,
  latitudeName,
  longitudeName,
  setValue,
  disabled,
  onCaptured,
}: CoordinateFieldsProps<T>) {
  const { t } = useTranslation();
  const { request, accuracy, errorKey, isLoading, isSupported } = useCurrentPosition();

  const setField = (name: FieldPath<T>, value: string) =>
    setValue(name, value as PathValue<T, FieldPath<T>>, { shouldValidate: true, shouldDirty: true });

  /**
   * Handle a value typed or pasted into either field. A pasted "lat, lon" pair is split across
   * both fields, which is how coordinates arrive when copied out of a map.
   */
  const handleChange = (raw: string, ownName: FieldPath<T>) => {
    const pair = parseCoordinatePair(raw);
    if (pair) {
      setField(latitudeName, pair.latitude);
      setField(longitudeName, pair.longitude);
    } else {
      setField(ownName, raw);
    }
    onCaptured?.(null);
  };

  const handleCapture = async () => {
    const position = await request();
    if (!position) return;
    setField(latitudeName, formatCoordinate(position.latitude));
    setField(longitudeName, formatCoordinate(position.longitude));
    onCaptured?.(position.capturedAt);
  };

  return (
    <Grid>
      <Grid.Col span={12}>
        <Stack gap={4}>
          <Text size="sm" color="secondary" data-testid="coordinates-label">
            {t("orders:pickupCoordinates")}
          </Text>
          <Text size="xs" color="tertiary" data-testid="coordinates-hint">
            {t("orders:coordinatesHint")}
          </Text>
        </Stack>
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <Controller
          control={control}
          name={latitudeName}
          render={({ field: { value, onBlur }, fieldState }) => (
            <TextInput
              data-testid="latitude-input"
              label={t("orders:latitude")}
              disabled={disabled}
              value={value ?? ""}
              onBlur={onBlur}
              onChange={(event) => handleChange(event.currentTarget.value, latitudeName)}
              error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          )}
        />
      </Grid.Col>

      <Grid.Col span={{ base: 12, md: 6 }}>
        <Controller
          control={control}
          name={longitudeName}
          render={({ field: { value, onBlur }, fieldState }) => (
            <TextInput
              data-testid="longitude-input"
              label={t("orders:longitude")}
              disabled={disabled}
              value={value ?? ""}
              onBlur={onBlur}
              onChange={(event) => handleChange(event.currentTarget.value, longitudeName)}
              error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
            />
          )}
        />
      </Grid.Col>

      {isSupported && (
        <Grid.Col span={12}>
          <Group gap="sm" align="center">
            <CTA
              intent="secondary"
              size="xs"
              type="button"
              disabled={disabled || isLoading}
              loading={isLoading}
              onClick={handleCapture}
              data-testid="use-my-location-btn"
            >
              {t("actions:useMyLocation")}
            </CTA>

            {/*
              Surface the browser's own accuracy radius. A phone with GPS reports single-digit
              metres; a desktop falls back to Wi-Fi or IP and can be kilometres out while looking
              just as authoritative. Showing it is what lets the user throw a bad fix away.
            */}
            {accuracy !== null &&
              (isCoarseAccuracy(accuracy) ? (
                <Text size="xs" color="warning" data-testid="coordinates-accuracy">
                  {t("orders:coordinatesAccuracyWarning", { accuracy: formatAccuracy(accuracy) })}
                </Text>
              ) : (
                <Text size="xs" color="success" data-testid="coordinates-accuracy">
                  {t("orders:coordinatesAccuracy", { accuracy: formatAccuracy(accuracy) })}
                </Text>
              ))}

            {errorKey && (
              <Text size="xs" color="error" data-testid="coordinates-location-error">
                {t(errorKey)}
              </Text>
            )}
          </Group>
        </Grid.Col>
      )}
    </Grid>
  );
}

export default CoordinateFields;
